import React, { useEffect, useRef, useState } from 'react'
import CoreBar, { CoreBarColorType } from '../../../../cvp-webgames-core/Components/ui/react/core-bar';
import { PreCacheItem, SinglePreCacheItem, PrecacheItemType } from '../../../../cvp-webgames-core/pre-caching/pre-cache-item';
import { PreCacheManager } from '../../../../cvp-webgames-core/pre-caching/pre-cache-manager';
import { AnimationState } from '../../../../cvp-webgames-core/UI/Animation/animation-base';
import { AnimationPage } from '../../../../cvp-webgames-core/UI/Animation/animation-page';
import { constants } from '../../../../misc/constants';
import { BaseConfig } from '../../../BaseConfig';
import PopUp1 from '../../../general/pop-up1';
import ProgressBar1 from '../../../general/progress-bar1';

import "./pre-load-assets-window.css"
import { enableKeyPress, getGlobalTextSizeStyle, setFocus, setLast508WindowId, TextHtmlTagType } from '../../../../misc/tools';

class PreLoadAssetsWindowCaching extends PreCacheItem {
	public getItems(): SinglePreCacheItem[] {
		return [
			{
				type: PrecacheItemType.Image,
				key: 'download',
				src: process.env.PUBLIC_URL + '/assets/images/download.svg'
			},
			{
				type: PrecacheItemType.Image,
				key: 'check',
				src: process.env.PUBLIC_URL + '/assets/images/check.svg'
			},
			{
				type: PrecacheItemType.Image,
				key: 'x',
				src: process.env.PUBLIC_URL + '/assets/images/x.svg'
			},
			{
				type: PrecacheItemType.Image,
				key: 'return1',
				src: process.env.PUBLIC_URL + '/assets/images/return1.svg'
			}
		];
	}
}

export const preLoadAssetsWindowCaching = new PreLoadAssetsWindowCaching();

enum WindowType {
	Initial,
	Confirm,
	Preload
}

enum AssetType {
	Image,
	Video,
	Audio
}

const title = 'Pre-Load-Assets'



const PreLoadAssetsWindow = (props: PreLoadAssetsWindowConfig) => {
	const root = useRef(null);
	const [focusSetPreloadInitial, setFocusSetPreloadInitial] = useState(false);
	const [focusSetPreloadConfirm, setFocusSetPreloadConfirm] = useState(false);
	const [focusSetPreloadPreload, setFocusSetPreloadPreload] = useState(false);
	useEffect(() => {
		props.animation.updateProps({
			page: root.current
		});
	}, [root])
	const [windowType, setWindowType] = useState(WindowType.Initial);
	const [completionPercentage, setCompletionPercentage] = useState(0);
	const [showDoneButton, setShowDneButton] = useState(false);

	const onPreloadCancelled = () => {
		props.preCacheManager.cancelLoadAllItems();
		props.onReturnToPreviousMenuClicked();
	}

	const onPrecacheStart = () => {
		setShowDneButton(false);
		props.preCacheManager.loadAllItems((percentage: number) => {
			setCompletionPercentage(percentage);
		}, () => {
			setShowDneButton(true)
		});
	}

	const renderInitial = () => {
		if(!focusSetPreloadInitial) {
			setTimeout(() => {
				setFocus('pre-load-initial-window-top');
			}, 1000);
			setFocusSetPreloadInitial(true);
		}

		return (
			<div ref={root} style={{
				position: 'absolute',
				top: '50%',
				left: '50%',
				width: '100%',
				height: '100%'
			}} className={props.animation.getInitialClass()}>
				<PopUp1
				title={constants.projectTitle}
				buttons={[
					{
						imgSrc: preLoadAssetsWindowCaching.getItemByKey('download').src,
						idFor508: 'pre-load-initial-window-top',
						alt508: '',
						width: '80%',
						height: '80px',
						buttonText: "Proceed to Pre-Load Assets",
						onClick: () => { 
							setLast508WindowId('pre-load-initial-window-top');

							setTimeout(() => {
								enableKeyPress();
							}, 10);

							setWindowType(WindowType.Confirm) 
						},
						hoverColor: constants.design.hoverColor2
					},
					{
						imgSrc: preLoadAssetsWindowCaching.getItemByKey('return1').src,
						alt508: '',
						width: '80%',
						height: '80px',
						buttonText: "Return to Previous Menu",
						hoverColor: constants.design.hoverColor2,
						onClick: props.onReturnToPreviousMenuClicked
					}
				]}
				onRenderContent={() => {
					return (
						<p className='default-text center-element-horizontal white-text' style={{
							textAlign: 'center',
							width: '100%',
							...getGlobalTextSizeStyle(TextHtmlTagType.p)
						}}>
							<span className='title-text2'>{title}</span><br />
							<br />
							This option allows you to pre-load all of the content for playing the game. Depending on your internet connection this process could take a long time. All of the files are kept on your local browser cache
						</p>);
				}}></PopUp1>
			</div>);
	}

	const renderConfirm = () => {
		if(!focusSetPreloadConfirm) {
			setTimeout(() => {
				setFocus('pre-load-confirm-window-top');
			}, 1000);
			setFocusSetPreloadConfirm(true);
		}
		return (<PopUp1
			title={constants.projectTitle}
			buttons={[
				{
					imgSrc: preLoadAssetsWindowCaching.getItemByKey('check').src,
					idFor508: 'pre-load-confirm-window-top',
					alt508: '',
					width: '80%',
					height: '80px',
					buttonText: "Yes, Pre-load the assets.",
					onClick: () => {
						setTimeout(() => {
							onPrecacheStart();
						}, 0);
						setWindowType(WindowType.Preload);
					}
				},
				{
					imgSrc: preLoadAssetsWindowCaching.getItemByKey('x').src,
					alt508: '',
					width: '80%',
					height: '80px',
					buttonText: "No, don't pre-load anything.",
					onClick: () => {
						setLast508WindowId('settings-window-top');

						props.onReturnToPreviousMenuClicked()
					}
				}
			]}
			onRenderContent={() => {
				return (
					<p className='default-text center-element-horizontal white-text' style={{
						textAlign: 'center',
						width: '100%',
						...getGlobalTextSizeStyle(TextHtmlTagType.p)
					}}>
						<span className='title-text2'>{title}</span><br />
						<br />
						Are you sure you want to pre-load the entire game? This download is approximately 1.5GB
					</p>);
			}}></PopUp1>);
	}

	const renderPreload = () => {
		if(!focusSetPreloadPreload) {
			setTimeout(() => {
				setFocus('pre-load-preload-window-top');
			}, 1000);
			setFocusSetPreloadPreload(true);
		}
		return (<PopUp1
			title={constants.projectTitle}
			buttons={[
				{
					imgSrc: showDoneButton ? preLoadAssetsWindowCaching.getItemByKey('check').src : preLoadAssetsWindowCaching.getItemByKey('x').src,
					idFor508: 'pre-load-preload-window-top',
					alt508: '',
					width: '80%',
					height: '80px',
					buttonText: showDoneButton ? "Done. Return to Previous Menu" : "Cancel Pre-Load.",
					onClick: showDoneButton ? props.onReturnToPreviousMenuClicked : onPreloadCancelled
				}
			]}
			onRenderContent={() => {
				return (
					<div>
						<p className='default-text center-element-horizontal white-text' style={{
							textAlign: 'center',
							width: '100%',
							...getGlobalTextSizeStyle(TextHtmlTagType.p)
						}}>
							<span className='title-text2'>{title}</span><br />
							<br />
							Hang in there! Once the game is pre-loaded the return to main menu button will be enabled.
							<br />
						</p>
						<br />
						<br />
						<div style={{
							position: 'relative',
							top: '0',
							left: '50%',
							width: '760px',
							height: '50px',
							transform: 'translateX(-50%)'
						}}>
							<CoreBar color={CoreBarColorType.blue} percentage={completionPercentage}></CoreBar>
						</div>
						<br />
						<br />
					</div>
				);
			}}></PopUp1>);
	}

	const renderContent = () => {
		switch (windowType) {
			case WindowType.Initial:
				return renderInitial();
			case WindowType.Confirm:
				return renderConfirm();
			case WindowType.Preload:
				return renderPreload();
		}
	}

	return (
		<div className="modal-disable-select center-element pre-load-assets-window-container" style={{
		}}>
			{renderContent()}
		</div>
	);

};

export interface PreLoadAssetsWindowConfig extends BaseConfig {
	animation: AnimationPage;
	onPreloadingFinished: () => void;
	preCacheManager: PreCacheManager;
	onReturnToPreviousMenuClicked: () => void;
}

export default PreLoadAssetsWindow;